import { z } from 'zod';
import { Paths } from 'type-fest';

import { diffDateTime, isBeforeDateTime } from '@occc-app/lib/date';
import { MAX_DAYS_IN_PAST } from '@occc-app/routes/Audience/constants';
import { IS_REQUIRED } from '@occc-app/lib/constants/validationMessages';
import { Audience_Purchase_Time_Window } from '@occc/graphql';
import { addCustomIssue as addIssue } from '../utils';
import { FixedWindowValues } from '../types';

export const fixedWindowSchema = z.preprocess(
  (value) => value ?? {},
  z
    .object({
      startDate: z.date(),
      endDate: z.date(),
    })
    .superRefine((data, ctx) => {
      const { startDate, endDate } = data;
      const now = new Date();
      let isStartDateValid = true;

      if (!startDate) {
        addIssue(ctx, 'startDate' satisfies Paths<FixedWindowValues>);
        isStartDateValid = false;
      } else if (diffDateTime(now, startDate, 'day') > MAX_DAYS_IN_PAST) {
        addIssue(
          ctx,
          'startDate' satisfies Paths<FixedWindowValues>,
          'Start date cannot be more than 90 days in the past',
        );
        isStartDateValid = false;
      }

      if (!endDate) {
        addIssue(ctx, 'endDate' satisfies Paths<FixedWindowValues>);
      } else if (isStartDateValid && !isBeforeDateTime(startDate, endDate)) {
        addIssue(
          ctx,
          'endDate' satisfies Paths<FixedWindowValues>,
          'End date must be after start date',
        );
      }
    }),
);

export const futureFixedWindowSchema = z.preprocess(
  (value) => value ?? {},
  z
    .object({
      startDate: z.date(),
      endDate: z.date(),
    })
    .superRefine((data, ctx) => {
      const { startDate, endDate } = data;
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      let isStartDateValid = true;

      if (!startDate) {
        addIssue(ctx, 'startDate' satisfies Paths<FixedWindowValues>);
        isStartDateValid = false;
      } else if (startDate < today) {
        addIssue(
          ctx,
          'startDate' satisfies Paths<FixedWindowValues>,
          'Start date cannot be in the past',
        );
        isStartDateValid = false;
      }

      if (!endDate) {
        addIssue(ctx, 'endDate' satisfies Paths<FixedWindowValues>);
      } else if (endDate < today) {
        addIssue(
          ctx,
          'endDate' satisfies Paths<FixedWindowValues>,
          'End date cannot be in the past',
        );
      } else if (isStartDateValid && !isBeforeDateTime(startDate, endDate)) {
        addIssue(
          ctx,
          'endDate' satisfies Paths<FixedWindowValues>,
          'End date must be after start date',
        );
      }
    }),
);

export const fixedMonthSchema = z.date({
  invalid_type_error: 'Value is not a valid date',
  required_error: IS_REQUIRED,
});

export const timeWindowSchema = z.nativeEnum(Audience_Purchase_Time_Window, {
  required_error: IS_REQUIRED,
  invalid_type_error: IS_REQUIRED,
});
